import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import useDebounce from 'src/utils/hook/useDebounce';

// import useDebounce from '@/utils/hooks/useDebounce';

export const useUrlMultiSearchState = <T extends Record<string, string | undefined>>(
	defaultValues: T,
	delay = 500,
) => {
	const router = useRouter();
	const [states, setStates] = useState<T | null>(null);

	// KEY 1: Prevent reading from the URL multiple times. Only load initial values from URL on the first render.
	const isHydrated = useRef(false);

	// KEY 2: Prevent the first debounce run (on initial load) from overwriting the URL with empty default values.
	const isFirstDebounce = useRef(true);

	// 1. HYDRATION: Sync URL query params to local state (only runs once)
	useEffect(() => {
		if (!router.isReady || isHydrated.current) return;

		const initialStates = { ...defaultValues };

		Object.keys(defaultValues).forEach((key) => {
			const urlValue = router.query[key];
			if (urlValue !== undefined) {
				initialStates[key as keyof T] = urlValue as T[keyof T];
			}
		});

		setStates(initialStates);
		isHydrated.current = true; // Mark as hydrated to prevent re-reading the URL
	}, [router.isReady, router.query]);

	const debouncedStates = useDebounce(states, delay);

	// 2. SYNC: Push debounced local state back to the URL query params
	useEffect(() => {
		if (!isHydrated.current) return;

		// Skip updating URL query params on the very first debounce cycle
		if (isFirstDebounce.current) {
			isFirstDebounce.current = false;
			return;
		}

		const currentQuery = { ...router.query };
		let hasChanged = false;

		Object.keys(defaultValues).forEach((key) => {
			const val = debouncedStates?.[key];

			if (val && val !== defaultValues[key]) {
				if (currentQuery[key] !== val) {
					currentQuery[key] = val;
					hasChanged = true;
				}
			} else {
				if (currentQuery[key] !== undefined) {
					delete currentQuery[key];
					hasChanged = true;
				}
			}
		});

		if (hasChanged) {
			router.replace({ pathname: router.pathname, query: currentQuery }, undefined, {
				shallow: true,
				scroll: false,
			});
		}
	}, [debouncedStates]);
	// ^ Depend only on debouncedStates. We don't need to respond to outside URL changes after hydration.

	const updateKey = (key: keyof T, value: string) => {
		setStates((prev) => ({ ...(prev || defaultValues), [key]: value }));
	};

	return {
		states,
		updateKey,
		debouncedStates,
		// Optional: can be used to display a skeleton UI or loading state before URL is synchronized
		isReady: isHydrated.current,
		// ideal for ssr
		query: router.query,
	};
};
